import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Users as UsersIcon, Search, RefreshCw, ChevronRight, ChevronDown,
  Layers, AlertTriangle, Check, ArrowRight, Download, UserCog, Shield
} from 'lucide-react';
import { apiClient, DirectoryRow, DirectoryResponse, AccountState } from '../lib/api';
import { UserAccessModal } from '../components/users/UserAccessModal';
import { useHierarchyMoves } from '../components/users/useHierarchyMoves';
import { toast } from '../lib/toast';

/**
 * One searchable directory of every account.
 *
 * The problem it solves: `status` has three values but six independent things
 * actually gate an account, and only `status` was ever on screen. So the pending
 * queue showed 11 people while 63 waited on an ID review, 22 waited on a network
 * assignment, and nobody could see at a glance who someone's AM was or where
 * they came from.
 *
 * Additive — the role-scoped pages (/physicians, /supervisors, /distributors,
 * /admins, /pending) are untouched and still work.
 */

const stateStyles: Record<AccountState, string> = {
  needs_approval: 'bg-amber-100 text-amber-800',
  awaiting_network: 'bg-orange-100 text-orange-800',
  awaiting_id_review: 'bg-blue-100 text-blue-800',
  onboarding: 'bg-indigo-100 text-indigo-700',
  never_logged_in: 'bg-gray-100 text-gray-600',
  active: 'bg-emerald-100 text-emerald-800',
  suspended: 'bg-red-100 text-red-700'
};

const stateLabels: Record<AccountState, string> = {
  needs_approval: 'Needs approval',
  awaiting_network: 'Awaiting network',
  awaiting_id_review: 'Awaiting ID review',
  onboarding: 'Onboarding',
  never_logged_in: 'Never logged in',
  active: 'Active',
  suspended: 'Suspended'
};

/** Urgency order, matching the server's sort so the chips read top-down. */
const STATE_ORDER: AccountState[] = [
  'needs_approval', 'awaiting_network', 'awaiting_id_review',
  'onboarding', 'never_logged_in', 'active', 'suspended'
];

/**
 * Account-type badges. Kept next to the role because they qualify it: a
 * "Clinic" that is a pharmacy fills outside doctors' scripts under its own NPI,
 * and one that is telemed-only prescribes through the network instead.
 */
function TypeBadges({ flags }: { flags: DirectoryRow['flags'] }) {
  if (!flags) return null;
  const badges = [
    flags.isPharmacy && { label: 'Pharmacy', cls: 'bg-purple-100 text-purple-800' },
    flags.isVet && { label: 'Vet', cls: 'bg-teal-100 text-teal-800' },
    flags.telemedOnly && { label: 'Telemed-only', cls: 'bg-cyan-100 text-cyan-800' }
  ].filter(Boolean) as Array<{ label: string; cls: string }>;
  if (!badges.length) return null;
  return (
    <span className="inline-flex gap-1 ml-1 align-middle">
      {badges.map(b => (
        <span key={b.label} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${b.cls}`}>
          {b.label}
        </span>
      ))}
    </span>
  );
}

const roleLabels: Record<string, string> = {
  physician: 'Clinic',
  distributor: 'AM',
  supervisor: 'Supervisor',
  admin: 'Admin'
};

/**
 * How long a blocker has been waiting, and how alarmed to be about it.
 *
 * The backend has always carried `since`; nothing rendered it, so a review filed
 * six days ago looked identical to one filed 215 days ago. In prod, 32 of 60 ID
 * reviews are over a month old — age is the difference between a queue and a
 * backlog.
 */
function ageFrom(since?: string): { label: string; cls: string } | null {
  if (!since) return null;
  const days = Math.floor((Date.now() - new Date(since).getTime()) / 86400000);
  if (days < 0) return null;                    // clock skew / bad data
  const label = days === 0 ? 'today' : days < 30 ? `${days}d` : `${Math.floor(days / 30)}mo`;
  const cls = days >= 90 ? 'bg-red-100 text-red-800'
    : days >= 30 ? 'bg-amber-100 text-amber-800'
    : 'text-gray-500';
  return { label, cls };
}

/** Where an account came from, from whichever fields are populated. */
/** /users/directory serves at most 2000 rows a page; the export walks pages. */
const EXPORT_PAGE_SIZE = 2000;

function provenanceLine(row: DirectoryRow): string {
  const p = row.provenance;
  const bits: string[] = [];
  if (p.registrationMethod) {
    bits.push({
      self_serve: 'Signed up via web form',
      invitation: 'Invited',
      admin_created: 'Created by admin'
    }[p.registrationMethod] || p.registrationMethod);
  }
  if (p.invitedByName) bits.push(`by ${p.invitedByName}`);
  if (p.referralSource) bits.push(`via ${p.referralSource}`);
  return bits.length ? bits.join(' ') : '';
}

function fmtDate(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * What an admin can do about a row, right now.
 *
 * Derived from the most urgent blocker they actually own. Blockers owned by the
 * clinic (ID proofing not started) get no button — offering one would imply we
 * can clear it, and a queue full of buttons that do nothing is worse than a
 * queue with none.
 */
type RowAction =
  | { kind: 'approve'; label: string }
  | { kind: 'review-id'; label: string }
  | { kind: 'open'; label: string }
  | { kind: 'waiting'; label: string }
  | null;

function primaryAction(row: DirectoryRow): RowAction {
  const has = (code: string) => row.blockers.some(b => b.code === code);
  if (has('status_pending')) return { kind: 'approve', label: 'Approve' };
  if (has('telemed_network_requested')) return { kind: 'open', label: 'Assign network' };
  if (has('network_gap_requested')) return { kind: 'open', label: 'Review gap' };
  if (has('id_proofing_submitted')) return { kind: 'review-id', label: 'Review ID' };
  if (has('credential_missing')) return { kind: 'open', label: 'Add credential' };
  if (has('id_proofing_not_started')) return { kind: 'waiting', label: 'Waiting on clinic' };
  return null;
}

export default function Users() {
  /**
   * `/pending` renders this same directory with the admin filter on.
   *
   * The old pending page listed `status: 'pending'` and nothing else — 11
   * accounts — while ID reviews, network assignments and missing credentials
   * were equally ours to clear and equally invisible. Reusing the directory
   * means the queue also inherits ageing, sorting, export and the per-row
   * actions instead of growing a second copy of them.
   */
  const location = useLocation();
  const pendingMode = location.pathname === '/pending';

  /**
   * Filters live in the URL so a view can be sent to someone.
   *
   * "The 19 that need you", "everything waiting on a network", "this
   * supervisor's org" were all reachable only by describing the clicks. The
   * URL is now the state, seeded from it on load and rewritten as it changes.
   *
   * Written with replace, not push: typing a search term would otherwise stack
   * a history entry per keystroke and make Back useless.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const param = (k: string, fallback = '') => searchParams.get(k) ?? fallback;

  const [data, setData] = useState<DirectoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(() => param('q'));
  const [debouncedQ, setDebouncedQ] = useState(() => param('q'));
  const [role, setRole] = useState(() => param('role'));
  const [state, setState] = useState(() => param('state'));
  const [hierarchy, setHierarchy] = useState(() => param('org', '1') === '1');
  const [sort, setSort] = useState(() => param('sort', 'urgency'));
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(() => parseInt(param('page', '1'), 10) || 1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // The full account modal, shared with the role-scoped pages. It loads
  // whatever the role needs itself, so All Users only has to say who.
  const [subject, setSubject] = useState<any | null>(null);
  // Impersonation confirmation modal — mirrors the role-scoped pages
  // (UserManagement.tsx) so the shared account modal's Impersonate button
  // behaves identically here.
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [impersonationReason, setImpersonationReason] = useState('');
  const [impersonationLoading, setImpersonationLoading] = useState(false);
  const [impersonationError, setImpersonationError] = useState<string | null>(null);
  // Show only what an admin can actually clear right now.
  const [needsMe, setNeedsMe] = useState(() =>
    searchParams.has('needs') ? param('needs') === '1' : pendingMode);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ) next.set('q', debouncedQ);
    if (role) next.set('role', role);
    if (state) next.set('state', state);
    if (sort !== 'urgency') next.set('sort', sort);
    if (!hierarchy) next.set('org', '0');
    if (page > 1) next.set('page', String(page));
    // Only pin `needs` when it differs from what the route already implies.
    if (needsMe !== pendingMode) next.set('needs', needsMe ? '1' : '0');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, role, state, sort, hierarchy, page, needsMe, pendingMode]);

  // Only the latest request may update the table: an answer to an older
  // search or page that arrives late is dropped.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++loadSeq.current;
    setLoading(true);
    const res = await apiClient.getUserDirectory({
      q: debouncedQ, role, state,
      needsAdmin: needsMe || undefined,
      sort,
      group: hierarchy ? 'hierarchy' : 'flat',
      page, limit: 50
    });
    if (mine !== loadSeq.current) return;
    if (res.success && res.data) setData(res.data);
    else toast.error(res.error || 'Failed to load users');
    setLoading(false);
  }, [debouncedQ, role, state, needsMe, sort, hierarchy, page]);

  useEffect(() => { load(); }, [load]);

  // Supervisor / AM moves from the account modal, shared with the role pages.
  const hierarchyMoves = useHierarchyMoves(subject, () => { load(); });

  /**
   * Approve outright from the row. The only blocker with a single unambiguous
   * resolution — everything else needs a human to look at something first, so
   * it opens the relevant surface instead of guessing.
   */
  const approve = async (row: DirectoryRow) => {
    setBusyId(row.id);
    const endpoint = row.role === 'physician'
      ? `/users/physicians/${row.id}/approve`
      : `/users/distributors/${row.id}/approve`;
    const res = await apiClient.put<{ success: boolean; error?: string }>(endpoint);
    if (res?.success) {
      toast.success(`Approved ${row.name}`);
      await load();
    } else {
      toast.error(res?.error || 'Failed to approve');
    }
    setBusyId(null);
  };

  const runAction = (row: DirectoryRow, action: RowAction) => {
    if (!action) return;
    if (action.kind === 'approve') return approve(row);
    // The review is inside the account modal now, not a second one.
    if (action.kind === 'review-id') return openDetail(row.id);
    if (action.kind === 'open') return openDetail(row.id);
  };

  /**
   * Open the shared account modal.
   *
   * Fetches the full user rather than synthesising a subject from the directory
   * row. The row carries only what the table needs, and the modal's read-only
   * views all read `subject.physicianData` — handing it a partial object made
   * every flag render as its default, so a pharmacy account that had just been
   * saved still displayed "Not a pharmacy".
   */
  const openDetail = async (id: string) => {
    setBusyId(id);
    const res = await apiClient.getUserProfile(id);
    if (res.success && res.data) {
      setSubject(res.data);
    } else {
      toast.error(res.error || 'Failed to load user');
    }
    setBusyId(null);
  };

  // Open the impersonation confirmation on top of the account modal.
  const handleOpenImpersonateModal = () => {
    setShowImpersonateModal(true);
    setImpersonationReason('');
    setImpersonationError(null);
  };

  const handleCancelImpersonation = () => {
    setShowImpersonateModal(false);
    setImpersonationReason('');
    setImpersonationError(null);
  };

  // Identical flow to UserManagement.tsx: swap the admin token for the
  // impersonation token, wipe cached auth, and hard-reload so the app
  // reinitialises as the impersonated user.
  const handleStartImpersonation = async () => {
    if (!subject) return;

    try {
      setImpersonationLoading(true);
      setImpersonationError(null);

      const response = await apiClient.startImpersonation(subject._id, impersonationReason || undefined);

      if (response.success && response.data) {
        // Save the admin's current token so we can restore it when stopping.
        const currentToken = localStorage.getItem('bloom_token');
        if (currentToken) {
          localStorage.setItem('bloom_admin_token', currentToken);
        }

        // Store the impersonation token under the key the API client reads.
        localStorage.setItem('bloom_token', response.data.token);

        // Clear cached auth state so the app loads fresh as the target user.
        localStorage.removeItem('bloom-auth');
        sessionStorage.clear();

        window.location.replace('/dashboard');
      } else {
        setImpersonationError(response.error || 'Failed to start impersonation');
      }
    } catch (error: any) {
      console.error('Error starting impersonation:', error);
      setImpersonationError('An unexpected error occurred');
    } finally {
      setImpersonationLoading(false);
    }
  };

  /**
   * Export everything the current filters match, not just the page.
   *
   * Walks every page rather than exporting `data.rows`: the table shows 50,
   * and the endpoint caps a page at 2000, so an export that stopped at a page
   * boundary would silently drop rows. Built client-side because the endpoint
   * is bearer-authenticated, so a plain download link would not carry the token.
   */
  const exportCsv = async () => {
    setExporting(true);
    const all: DirectoryRow[] = [];
    for (let exportPage = 1; ; exportPage++) {
      const res = await apiClient.getUserDirectory({
        q: debouncedQ, role, state,
        needsAdmin: needsMe || undefined,
        sort, group: 'flat', page: exportPage, limit: EXPORT_PAGE_SIZE
      });
      if (!res.success || !res.data) {
        toast.error(res.error || 'Export failed');
        setExporting(false);
        return;
      }
      all.push(...res.data.rows);
      if (res.data.rows.length === 0 || all.length >= res.data.total || exportPage >= 100) break;
    }
    const cell = (v: unknown) => {
      const t = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    const header = ['Name', 'Email', 'Role', 'Clinic', 'Phone', 'State', 'Blockers',
      'Waiting since', 'AM', 'Supervisor', 'Types', 'Catalogs', '30d orders',
      'Percentile', 'Last login', 'Source', 'Created'];
    const rows = all.map(r => [
      r.name, r.email, r.role, r.clinicName, r.phone, r.state,
      r.blockers.map(b => b.label).join(' | '),
      r.blockers.map(b => b.since ? new Date(b.since).toISOString().slice(0, 10) : '').filter(Boolean).join(' | '),
      r.am.name, r.supervisor?.name,
      [r.flags?.isPharmacy && 'Pharmacy', r.flags?.isVet && 'Vet', r.flags?.telemedOnly && 'Telemed-only'].filter(Boolean).join(' | '),
      r.catalogs.map(c => c.name).join(' | '),
      r.volume30d?.orders ?? 0, r.volume30d?.percentile ?? '',
      r.lastLoginAt ? new Date(r.lastLoginAt).toISOString().slice(0, 10) : '',
      provenanceLine(r),
      r.provenance.createdAt ? new Date(r.provenance.createdAt).toISOString().slice(0, 10) : ''
    ]);
    const csv = [header, ...rows].map(r => r.map(cell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `bloom-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} rows`);
    setExporting(false);
  };

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  // Every blocker the server reports as ours, across the whole population —
  // the facet counts already cover the full set, so this is not page-local.
  const adminActionable = useMemo(() => {
    if (!data) return 0;
    const owned = ['status_pending', 'telemed_network_requested', 'network_gap_requested',
                   'id_proofing_submitted', 'credential_missing'];
    return owned.reduce((n, code) => n + (data.facets.blockers[code] || 0), 0);
  }, [data]);

  const visibleRows = data?.rows ?? [];

  // Only the states that actually occur, in urgency order.
  const stateChips = useMemo(() => {
    if (!data) return [];
    return STATE_ORDER
      .map(s => ({ state: s, count: data.facets.states[s] || 0 }))
      .filter(c => c.count > 0);
  }, [data]);

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <UsersIcon className="w-6 h-6" /> {pendingMode ? 'Pending review' : 'Users'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {pendingMode
              ? 'Everything waiting on us — approvals, ID reviews, network assignments and credentials.'
              : "Every account, what it's waiting on, and who owns it."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50"
            title="Export every row matching the current filters"
          >
            <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Search + filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search name, clinic, email or NPI…"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md text-sm"
            />
          </div>
          <select
            value={role}
            onChange={e => { setRole(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-md text-sm"
          >
            <option value="">All roles</option>
            {Object.entries(roleLabels).map(([k, label]) => (
              <option key={k} value={k}>
                {label}{data?.facets.roles[k] ? ` (${data.facets.roles[k]})` : ''}
              </option>
            ))}
          </select>
          <select
            value={sort}
            onChange={e => { setSort(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-md text-sm"
            title="Sort order"
          >
            <option value="urgency">Most urgent first</option>
            <option value="oldestBlocker">Longest waiting</option>
            <option value="volume">Highest 30d volume</option>
            <option value="lastLogin">Recently active</option>
            <option value="name">Name (A–Z)</option>
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 px-3 py-2 border border-gray-200 rounded-md cursor-pointer">
            <input
              type="checkbox"
              checked={hierarchy}
              onChange={e => setHierarchy(e.target.checked)}
            />
            <Layers className="w-4 h-4" /> Show org below
          </label>
          {/* The fastest route to "what can I clear right now". */}
          <label className={`inline-flex items-center gap-2 text-sm px-3 py-2 border rounded-md cursor-pointer ${
            needsMe ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-gray-200 text-gray-700'
          }`}>
            <input
              type="checkbox"
              checked={needsMe}
              onChange={e => { setNeedsMe(e.target.checked); setPage(1); }}
            />
            <AlertTriangle className="w-4 h-4" /> Needs me
            {data && <span className="text-xs opacity-70">({adminActionable})</span>}
          </label>
        </div>

        {/* State chips double as the "what needs attention" summary. */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setState(''); setPage(1); }}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
              state === '' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600'
            }`}
          >
            All {data ? data.facets.roles && Object.values(data.facets.states).reduce((a, b) => a + b, 0) : ''}
          </button>
          {stateChips.map(({ state: s, count }) => (
            <button
              key={s}
              onClick={() => { setState(state === s ? '' : s); setPage(1); }}
              className={`px-2.5 py-1 rounded-full text-xs font-medium ${stateStyles[s]} ${
                state === s ? 'ring-2 ring-offset-1 ring-gray-900' : ''
              }`}
            >
              {stateLabels[s]} {count}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Role</th>
                <th className="text-left px-4 py-2 font-medium">State</th>
                <th className="text-left px-4 py-2 font-medium">Waiting on</th>
                <th className="text-left px-4 py-2 font-medium">AM</th>
                <th className="text-left px-4 py-2 font-medium">30d volume</th>
                <th className="text-left px-4 py-2 font-medium">Last login</th>
                <th className="text-right px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">Loading…</td></tr>
              )}
              {!loading && visibleRows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                  {needsMe ? 'Nothing is waiting on you here.' : 'No users match those filters.'}
                </td></tr>
              )}
              {!loading && visibleRows.map(row => (
                <UserRow
                  key={row.id}
                  row={row}
                  depth={0}
                  expanded={expanded}
                  onToggle={toggle}
                  onOpen={openDetail}
                  onAction={runAction}
                  busyId={busyId}
                />
              ))}
            </tbody>
          </table>
        </div>

        {data && data.total > data.limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
            <span className="text-gray-500">
              {(data.page - 1) * data.limit + 1}–{Math.min(data.page * data.limit, data.total)} of {data.total}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >Previous</button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {subject && (
        <UserAccessModal
          subject={subject}
          onClose={() => { hierarchyMoves.cancel(); setSubject(null); }}
          // Deep link from the hierarchy: swap the modal to that account
          // rather than closing and making them search for it.
          onOpenUser={openDetail}
          onImpersonate={handleOpenImpersonateModal}
          onSubjectPatched={(patch) => setSubject((prev: any) => (prev ? { ...prev, ...patch } : prev))}
          {...hierarchyMoves.modalProps}
        />
      )}

      {hierarchyMoves.modals}

      {showImpersonateModal && subject && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <UserCog className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Start Impersonation</h3>
                  <p className="text-sm text-gray-500">You will view Bloom as this user</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4">
              {impersonationError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="text-sm text-red-800">{impersonationError}</div>
                </div>
              )}

              <div className="space-y-4">
                {/* Target User Info */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">You are about to impersonate:</div>
                  <div className="font-medium text-gray-900">
                    {subject.profile?.firstName} {subject.profile?.lastName}
                  </div>
                  <div className="text-sm text-gray-600">{subject.email}</div>
                  <div className="mt-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      subject.role === 'distributor'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {subject.role === 'distributor' ? 'Account Manager' :
                       subject.role === 'physician' ? 'Physician' :
                       subject.role === 'physician_readonly' ? 'Physician (Read-only)' : subject.role}
                    </span>
                  </div>
                </div>

                {/* Reason Input */}
                <div>
                  <label htmlFor="impersonation-reason" className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for impersonation <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    id="impersonation-reason"
                    value={impersonationReason}
                    onChange={(e) => setImpersonationReason(e.target.value)}
                    placeholder="e.g., Debugging order issue, Customer support request..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* Security Warning */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <Shield className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-amber-800 mb-1">Security Notice</div>
                      <ul className="text-amber-700 space-y-1">
                        <li>• All actions will be logged for audit purposes</li>
                        <li>• Your admin identity will be recorded</li>
                        <li>• Session will be visible on the Impersonation Dashboard</li>
                        <li>• A banner will display while impersonating</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCancelImpersonation}
                disabled={impersonationLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleStartImpersonation}
                disabled={impersonationLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {impersonationLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Starting...
                  </>
                ) : (
                  <>
                    <UserCog className="w-4 h-4 mr-2" />
                    Start Impersonation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/**
 * One row, plus its org beneath it when hierarchy mode is on. Rendered
 * recursively so a supervisor → AM → clinic chain nests to arbitrary depth
 * without three near-identical row components.
 */
function UserRow({ row, depth, expanded, onToggle, onOpen, onAction, busyId }: {
  row: DirectoryRow;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onOpen: (id: string) => void;
  onAction: (row: DirectoryRow, action: RowAction) => void;
  busyId: string | null;
}) {
  const action = primaryAction(row);
  const hasChildren = !!row.children?.length;
  const isOpen = expanded.has(row.id);

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => onOpen(row.id)}
      >
        <td className="px-4 py-2">
          <div className="flex items-center gap-1" style={{ paddingLeft: depth * 20 }}>
            {hasChildren ? (
              <button
                onClick={e => { e.stopPropagation(); onToggle(row.id); }}
                className="p-0.5 text-gray-400 hover:text-gray-700"
              >
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            ) : <span className="w-5" />}
            <button onClick={e => { e.stopPropagation(); onOpen(row.id); }} className="text-left">
              <span className="font-medium text-gray-900 hover:underline">{row.name}</span>
              {row.clinicName && row.clinicName !== row.name && (
                <span className="block text-xs text-gray-500">{row.clinicName}</span>
              )}
              <span className="block text-xs text-gray-500">{row.email}</span>
            </button>
            {hasChildren && (
              <span className="ml-2 text-xs text-gray-400">({row.children!.length})</span>
            )}
          </div>
        </td>
        <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
          {roleLabels[row.role] || row.role}
          <TypeBadges flags={row.flags} />
        </td>
        <td className="px-4 py-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stateStyles[row.state]}`}>
            {stateLabels[row.state]}
          </span>
        </td>
        <td className="px-4 py-2">
          {row.blockers.length === 0 ? <span className="text-gray-400">—</span> : (
            <div className="space-y-0.5">
              {row.blockers.map(b => {
                const age = ageFrom(b.since);
                return (
                  <div key={b.code} className="flex items-center gap-1.5 text-xs">
                    {b.owner === 'admin'
                      ? <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                      : <span className="w-3 shrink-0" />}
                    <span className={b.owner === 'admin' ? 'text-gray-800' : 'text-gray-500'}>
                      {b.action}
                    </span>
                    {age && (
                      <span
                        className={`px-1 rounded text-[10px] font-medium shrink-0 ${age.cls}`}
                        title={`Waiting since ${fmtDate(b.since)}`}
                      >
                        {age.label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </td>
        <td className="px-4 py-2">
          {row.am.isHouse
            // gray, not slate: the dark-mode overrides in index.css remap the
            // gray/stone ramps only, so a slate chip stays light-on-light.
            ? <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-medium">House</span>
            : <span className="text-gray-700">{row.am.name}</span>}
        </td>
        <td className="px-4 py-2"><VolumeCell volume={row.volume30d} /></td>
        <td className="px-4 py-2 text-gray-500">{fmtDate(row.lastLoginAt)}</td>
        <td className="px-4 py-2 text-right whitespace-nowrap">
          {!action && (
            <button
              onClick={e => { e.stopPropagation(); onOpen(row.id); }}
              className="text-xs text-gray-500 hover:text-gray-800 underline"
            >View</button>
          )}
          {action?.kind === 'waiting' && (
            // Nothing for us to do; say so rather than offer a dead button.
            <span className="text-xs text-gray-400">{action.label}</span>
          )}
          {action && action.kind !== 'waiting' && (
            <button
              onClick={e => { e.stopPropagation(); onAction(row, action); }}
              disabled={busyId === row.id}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium disabled:opacity-50 ${
                action.kind === 'approve'
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
              }`}
            >
              {busyId === row.id
                ? <span className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
                : action.kind === 'approve' ? <Check className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
              {action.label}
            </button>
          )}
        </td>
      </tr>

      {isOpen && row.children?.map(child => (
        <UserRow
          key={child.id}
          row={child}
          depth={depth + 1}
          expanded={expanded}
          onToggle={onToggle}
          onOpen={onOpen}
          onAction={onAction}
          busyId={busyId}
        />
      ))}
    </>
  );
}

/**
 * Trailing-30-day orders with a percentile bar.
 *
 * The percentile is against peers who ALSO have volume, not the whole role —
 * most clinics don't order in a given month, so ranking against everyone would
 * park a single order near the top and tell you nothing. Zero volume shows a
 * dash rather than "0th percentile", which would read as a ranking it isn't.
 */
function VolumeCell({ volume }: { volume: { orders: number; percentile: number | null } }) {
  if (!volume || volume.orders === 0) {
    return <span className="text-gray-400 text-sm">—</span>;
  }
  const pct = volume.percentile;
  // Top quartile is worth spotting at a glance; bottom quartile is worth asking about.
  const tone = pct === null ? 'bg-gray-300'
    : pct >= 75 ? 'bg-emerald-500'
    : pct >= 25 ? 'bg-blue-400'
    : 'bg-gray-400';

  return (
    <div className="min-w-[92px]">
      <div className="flex items-baseline gap-1.5">
        <span className="font-medium text-gray-900 tabular-nums">{volume.orders}</span>
        {pct !== null && <span className="text-xs text-gray-500">p{pct}</span>}
      </div>
      {pct !== null && (
        <div className="mt-1 h-1 w-full bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.max(pct, 3)}%` }} />
        </div>
      )}
    </div>
  );
}

